import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, combineLatest, EMPTY, filter, finalize, first, map, Observable, shareReplay, switchMap, tap } from 'rxjs';

import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { LoanStatus } from '../cmg-1003/models';
import { SessionQuery } from '../core/state';
import { SpinnerService } from '../shared/services/spinner.service';
import { isNullish } from '../shared/utils';
import { IrsForm4506, IrsForm4506LoanModel, UpdateIrsForm4506LoanModelRequest } from './form-4506c.models';
import { REQUIRES_SECURITY_PROFILE } from '../interceptors';

@Injectable({ providedIn: 'root' })
export class Form4506CService {
  private readonly _http = inject(HttpClient);
  private readonly _environment = inject(APP_ENV);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _spinnerService = inject(SpinnerService);

  /** Refresh data from API. Value indicates whether to show spinner. */
  refresh$ = new BehaviorSubject<boolean>(false);
  fileDataId$ = this._sessionQuery.select((t) => t.fileDataId).pipe(filter((fileDataId) => !!fileDataId));
  irsForm4506$ = combineLatest([this.fileDataId$, this.refresh$]).pipe(
    switchMap(([fileDataId, showSpinner]) => this.getIrsForm4506(fileDataId, showSpinner)),
    shareReplay(1),
  );
  disabled$ = this._sessionQuery
    .select((s) => s.loanStatusId)
    .pipe(map((loanStatus) => ![LoanStatus.Prospect, LoanStatus.LePending, LoanStatus.CreditOnly, LoanStatus.PreApproved].includes(loanStatus)));
  update$ = new BehaviorSubject<IrsForm4506[] | null>(null);

  save(): Observable<IrsForm4506LoanModel> {
    return combineLatest([this.irsForm4506$, this.update$]).pipe(
      first(),
      switchMap(([value, irsForm4506s]) =>
        isNullish(irsForm4506s)
          ? EMPTY
          : this.saveIrsForm4506({
              fileDataId: value.fileDataId,
              irsForm4506s,
              deletedIrsForm4506Ids: value.irsForm4506s
                .map((form) => form.irsForm4506Id)
                .filter((id) => !irsForm4506s.some((form) => form.irsForm4506Id === id)),
            }).pipe(
              tap(() => {
                this.update$.next(null);
                this.refresh$.next(true);
              }),
            ),
      ),
    );
  }

  getIrsForm4506(fileDataId: number, showSpinner: boolean): Observable<IrsForm4506LoanModel> {
    const request = this._http.get<IrsForm4506LoanModel>(`${this._environment.retail.apiUrl}/irs-forms/irs-form-4506-loan-model/${fileDataId}`, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
    if (!showSpinner) {
      return request;
    }

    this._spinnerService.show('Loading Form 4506-C...');
    return request.pipe(
      finalize(() => {
        this._spinnerService.hide();
      }),
    );
  }

  saveIrsForm4506(data: UpdateIrsForm4506LoanModelRequest): Observable<IrsForm4506LoanModel> {
    return this._http.post<IrsForm4506LoanModel>(`${this._environment.retail.apiUrl}/irs-forms/irs-form-4506-loan-model`, data, {
      context: new HttpContext().set(REQUIRES_SECURITY_PROFILE, true),
    });
  }
}
